import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Tooltip from 'react-bootstrap/Tooltip'
import Overlay from 'react-bootstrap/Overlay'
import { useTranslation } from 'react-i18next'
import { getFirstPartOfURLPath } from 'utils/helpers'
import { MultiSelect } from 'components/common/multi-select'
import { amplitudeEvent, UserEvents } from 'config/amplitude'
import styles from './StatusDropdown.module.scss'
import classNames from 'classnames/bind'
import {
  getGroupedPriceChangeStatusesForFilter,
  getPriceChangeStatusesForAGivenLocale,
} from './constants'
import { useCompanyTimeZone } from 'components/common/hooks/useCompanyTimeZone'

const cx = classNames.bind(styles)

const StatusDropdown = ({ value, onValueChange, dataTestId }) => {
  const { companyTimeZone } = useCompanyTimeZone()
  const { t } = useTranslation()

  const isCompanyLondonTimezone = companyTimeZone === 'Europe/London'
  const allStatuses = getPriceChangeStatusesForAGivenLocale(isCompanyLondonTimezone, t)
  const groupedStatusesForFilter = getGroupedPriceChangeStatusesForFilter(isCompanyLondonTimezone, t)

  const items = groupedStatusesForFilter.map(item => item.key).slice() // Contains 'parent' action groups
  const resolvedItems = allStatuses.map(item => item.key) // Only contains 'real' statuses sent to backend
  value = value.filter(v => resolvedItems.includes(v))

  const [tooltipTarget, setTooltipTarget] = useState(null)

  useEffect(() => {
    if (tooltipTarget) {
      const timeout = setTimeout(() => {
        setTooltipTarget(null)
      }, 3000)
      return () => clearTimeout(timeout)
    }
  }, [tooltipTarget])

  const handleLessThanMinSelected = event => {
    setTooltipTarget(event.target)
  }

  const handleTooltipClick = () => {
    setTooltipTarget(null)
  }

  const handleDropdownClick = open => {
    !open &&
      amplitudeEvent(
        UserEvents.PerformanceReport.GRADES_FILTER_SELECTED(
          getFirstPartOfURLPath(window.location.pathname)
        )
      )
  }

  return (
    <>
      <MultiSelect
        placeholder={t('widgets.polePriceAudit.statusDropdownNoneSelected')}
        allItemsText={t('widgets.polePriceAudit.statusDropdownAllSelected')}
        items={items}
        value={value}
        minSelected={1}
        onLessThanMinSelected={handleLessThanMinSelected}
        onValueSelect={onValueChange}
        onDropdownClick={handleDropdownClick}
        itemDefinitions={groupedStatusesForFilter}
        webAssetsKey={'polePriceAuditStatuses'}
        dataTestId={dataTestId}
        maxHeight='400px'
      />
      <Overlay target={tooltipTarget} show={!!tooltipTarget} position="top">
        <Tooltip className={cx('tooltip')} onClick={handleTooltipClick}>
          <div className={cx('tooltip-content')} data-testid="tooltip">
            {t('widgets.polePriceAudit.statusDropdownTooltip')}
          </div>
        </Tooltip>
      </Overlay>
    </>
  )
}

StatusDropdown.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onValueChange: PropTypes.func,
  dataTestId: PropTypes.string,
}

StatusDropdown.defaultProps = {
  value: [],
  onValueChange: null,
}

export { StatusDropdown }
